import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Phone, Mail, MapPin, MessageCircle, CheckCircle, AlertCircle, Clock } from 'lucide-react';

// WhatsApp Konfigürasyonu
const WHATSAPP_CONFIG = {
  number: '+905524278605',
  message: 'Merhaba, GEVEZ SOFTWARE hizmetleri hakkında bilgi almak istiyorum.',
};

// İletişim Bilgileri
const CONTACT_INFO = {
  phone: '+90 (552) 427 86 05',
  email: 'info@gevezsoftware.com',
  address: 'Mersin, Türkiye',
  hours: 'Pazartesi - Cuma: 09:00 - 18:00',
};

function ContactPage() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Web Siteden Yeni Mesaj',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'İsim alanı zorunludur';
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Mesaj alanı zorunludur';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Mesaj en az 10 karakter olmalıdır';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    // --- AKILLI API ADRESİ ---
    // Eğer localhost'taysan 8000 portuna, internetteysen kendi domainine gider.
    const API_ENDPOINT = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://127.0.0.1:8000/api/contact'
      : 'https://gevezsoftware.com/api/contact';

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: 'Web Siteden Yeni Mesaj', message: '' });
      } else {
        setSubmitStatus('error');
        console.error("Backend Hatası:", result.detail);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error("Bağlantı Hatası:", error);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_CONFIG.number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(WHATSAPP_CONFIG.message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      {/* Hero Alanı */}
      <section className="py-16 md:py-24 bg-background-primary relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
            İletişim
          </motion.p>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-6">
            Projenizi <span className="gradient-text">Konuşalım</span>
          </h1>
        </div>
      </section>

      {/* İletişim İçeriği */}
      <section ref={sectionRef} className="py-16 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Sol Kolon: Bilgiler */}
            <motion.div initial={{ opacity: 0, x: -50 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
              <h2 className="font-display text-2xl font-bold text-white mb-8">Bize Ulaşın</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-lg bg-brand-cyan/10 flex items-center justify-center group-hover:bg-brand-cyan/20 transition-colors">
                    <Phone className="w-5 h-5 text-brand-cyan" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm font-mono">Telefon</p>
                    <p className="text-white font-mono">{CONTACT_INFO.phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-lg bg-brand-cyan/10 flex items-center justify-center group-hover:bg-brand-cyan/20 transition-colors">
                    <Mail className="w-5 h-5 text-brand-cyan" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm font-mono">E-posta</p>
                    <p className="text-white font-mono">{CONTACT_INFO.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-lg bg-brand-cyan/10 flex items-center justify-center group-hover:bg-brand-cyan/20 transition-colors">
                    <MapPin className="w-5 h-5 text-brand-cyan" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm font-mono">Adres</p>
                    <p className="text-white font-mono">{CONTACT_INFO.address}</p>
                  </div>
                </div>
              </div>

              <motion.button 
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }}
                onClick={openWhatsApp} 
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white font-display font-bold text-sm uppercase tracking-wider rounded-lg hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transition-all duration-300 mb-10"
              >
                <MessageCircle className="w-5 h-5" /> WhatsApp ile İletişim
              </motion.button>

              <div className="rounded-lg overflow-hidden h-64 border border-white/10 relative group shadow-2xl">
                <iframe
                  title="Mersin Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d102434.37243958992!2d34.54227461680786!3d36.78278278297775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1527f37478052529%3A0x6a05e2d53922d42!2sMersin!5e0!3m2!1str!2str!4v1712354400000!5m2!1str!2str"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(1) invert(0.9) contrast(1.2) brightness(0.8)' }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
                
                <div className="absolute top-4 right-4 bg-background-primary/90 backdrop-blur-md px-4 py-2 rounded-md border border-brand-cyan shadow-[0_0_15px_rgba(0,229,255,0.4)] flex items-center gap-2 z-10">
                  <MapPin size={16} className="text-brand-cyan animate-pulse" />
                  <span className="text-xs font-mono text-white font-bold uppercase tracking-widest">Mersin, Türkiye</span>
                </div>
              </div>
            </motion.div>

            {/* Sağ Kolon: Form */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
              <form onSubmit={handleSubmit} className="glass p-8 rounded-lg border border-white/5 hover:border-brand-cyan/20 transition-all duration-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-text-secondary text-sm font-mono mb-2">İsim Soyisim *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      className={`w-full bg-white/5 border-b ${errors.name ? 'border-red-500' : 'border-white/20'} px-4 py-3 text-white font-mono focus:outline-none focus:border-brand-cyan transition-all`} 
                      placeholder="Adınız" 
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-2 font-mono">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-text-secondary text-sm font-mono mb-2">Telefon</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      className="w-full bg-white/5 border-b border-white/20 px-4 py-3 text-white font-mono focus:outline-none focus:border-brand-cyan transition-all" 
                      placeholder="+90 5XX XXX XX XX" 
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-text-secondary text-sm font-mono mb-2">E-posta *</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    className={`w-full bg-white/5 border-b ${errors.email ? 'border-red-500' : 'border-white/20'} px-4 py-3 text-white font-mono focus:outline-none focus:border-brand-cyan transition-all`} 
                    placeholder="email@example.com" 
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-2 font-mono">{errors.email}</p>}
                </div>

                <div className="mb-8">
                  <label className="block text-text-secondary text-sm font-mono mb-2">Mesajınız *</label>
                  <textarea 
                    name="message" 
                    value={formData.message} 
                    onChange={handleChange} 
                    rows={5} 
                    className={`w-full bg-white/5 border-b ${errors.message ? 'border-red-500' : 'border-white/20'} px-4 py-3 text-white font-mono focus:outline-none focus:border-brand-cyan transition-all resize-none`} 
                    placeholder="Projeniz hakkında bilgi verin..." 
                  />
                  {errors.message && <p className="text-red-400 text-xs mt-2 font-mono">{errors.message}</p>}
                </div>

                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  type="submit" 
                  disabled={isSubmitting} 
                  className={`w-full flex items-center justify-center gap-3 py-4 font-display font-bold text-sm uppercase tracking-wider transition-all duration-300 rounded-sm ${isSubmitting ? 'bg-brand-cyan/50 cursor-not-allowed' : 'bg-brand-cyan hover:shadow-[0_0_40px_rgba(0,229,255,0.6)]'} text-background-primary`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                       <div className="w-4 h-4 border-2 border-background-primary/30 border-t-background-primary rounded-full animate-spin" />
                       İşleniyor...
                    </div>
                  ) : "Mesaj Gönder"}
                </motion.button>

                {submitStatus === 'success' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
                    <CheckCircle className="text-green-400" size={18} />
                    <p className="text-green-400 font-mono text-xs">Mesajınız başarıyla iletildi</p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
                    <AlertCircle className="text-red-400" size={18} />
                    <p className="text-red-400 font-mono text-xs">Bağlantı kurulamadı. Backend çalışıyor mu kanka?</p>
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;